import React, { useState } from "react";
import { ModerniseHeader } from "@/components/organisms/modernise-with-ai/ModerniseHeader";
import { QuickActionsSection } from "@/components/molecules/modernise-with-ai/QuickActionsSection";
import { ModernisationToolsSection } from "@/components/organisms/modernise-with-ai/ModernisationToolsSection";
import { RequestToolModal } from "@/components/organisms/modernise-with-ai/RequestToolModal";
import { ErrorAnalysisModal } from "@/components/organisms/modernise-with-ai/ErrorAnalysisModal";
import { ModernisationResults } from "@/components/organisms/modernise-with-ai/ModernisationResults";
import { categories } from "@/data/modernizationTools";
import { ComponentPreviewScreen } from "@/components/organisms/modernise-with-ai/ComponentPreviewScreen";

export const ModerniseWithAI: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedTools, setSelectedTools] = useState<any[]>([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [currentScreen, setCurrentScreen] = useState<
    "tools" | "preview" | "results"
  >("tools");
  const [finalResults, setFinalResults] = useState<any>(null);
  const [componentSelection, setComponentSelection] = useState<any[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);

  /** ---------------- TOOLS → PREVIEW ---------------- **/
  const handleAnalyzeSelected = () => {
    if (selectedTools.length === 0) return;

    setIsAnalyzing(true);
    setAnalysisProgress(0);

    const interval = setInterval(() => {
      setAnalysisProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsAnalyzing(false);

          // Generate mock component previews
          const mockComponents = selectedTools.flatMap((tool) =>
            Array.from({ length: 5 }, (_, i) => ({
              id: `${tool.id}-comp-${i}`,
              name: `${tool.title.split(" ")[0]} Component ${i + 1}`,
              type: tool.category,
              status: "Ready for modernization",
              selected: true,
              details: `Legacy component that can be modernized using ${tool.title}`,
            }))
          );

          setComponentSelection(mockComponents);
          setCurrentScreen("preview");
          return 100;
        }
        return prev + 10;
      });
    }, 200);
  };

  /** ---------------- PREVIEW → RESULTS ---------------- **/
  const handleStartModernization = () => {
    const selectedComponents = componentSelection.filter((c) => c.selected);
    if (selectedComponents.length === 0) return;

    setIsAnalyzing(true);
    setAnalysisProgress(0);

    const interval = setInterval(() => {
      setAnalysisProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setIsAnalyzing(false);

          // Mock final results
          setFinalResults({
            totalProcessed: selectedComponents.length,
            successful: Math.floor(selectedComponents.length * 0.85),
            failed: Math.floor(selectedComponents.length * 0.15),
            performanceGain: Math.floor(Math.random() * 40) + 30,
            codeReduction: Math.floor(Math.random() * 30) + 20,
          });
          setCurrentScreen("results");
          return 100;
        }
        return prev + 10;
      });
    }, 300);
  };

  /** ---------------- RESET ---------------- **/
  const handleReset = () => {
    setCurrentScreen("tools");
    setSelectedTools([]);
    setComponentSelection([]);
    setFinalResults(null);
    setIsAnalyzing(false);
    setAnalysisProgress(0);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {currentScreen === "tools" && (
          <>
            <ModerniseHeader
              selectedTools={selectedTools}
              onAnalyze={handleAnalyzeSelected}
            />

            <QuickActionsSection
              onActionClick={(action) => {
                if (action === "request") setShowRequestModal(true);
                if (action === "error") setShowErrorModal(true);
              }}
            />

            <ModernisationToolsSection
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              selectedTools={selectedTools}
              onToolToggle={setSelectedTools}
            />
          </>
        )}

        <RequestToolModal
          open={showRequestModal}
          onClose={() => setShowRequestModal(false)}
          form={{
            toolName: "",
            description: "",
            importance: "",
            useCase: "",
          }}
          onChange={() => {}}
          onSubmit={() => {}}
        />
        <ErrorAnalysisModal
          open={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          errorFile={null}
          onFileSelect={() => {}}
          onSubmit={() => {}}
        />

        {currentScreen === "preview" && (
          <ComponentPreviewScreen
            componentSelection={componentSelection}
            setComponentSelection={setComponentSelection}
            setCurrentScreen={setCurrentScreen}
            isAnalyzing={isAnalyzing}
            analysisProgress={analysisProgress}
            handleStartModernization={handleStartModernization}
          />
        )}

        {currentScreen === "results" && (
          <ModernisationResults
            results={finalResults}
            onReset={() => setCurrentScreen("tools")}
          />
        )}
      </div>
    </div>
  );
};

export default ModerniseWithAI;
